import { useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { AppLayout } from '@/layout/AppLayout';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { RouteNextSteps } from '@/components/content/RouteNextSteps';
import { applyRouteMeta } from '@/lib/meta';
import type { RouteDefinition } from '@/routes';
import { contentRoutes, staticContentLinks } from '@/routes';

type Props = {
  route: RouteDefinition;
};

export function GuidesHubPage({ route }: Props) {
  useEffect(() => {
    applyRouteMeta(route);
  }, [route]);

  const guideRoutes = useMemo(
    () => contentRoutes.filter((contentRoute) => contentRoute.path.startsWith('/guides/')),
    []
  );

  return (
    <AppLayout>
      <section className="hero-shell rounded-2xl border border-border p-6 md:p-8">
        <p className="text-xs font-semibold uppercase tracking-[0.16em] text-muted-foreground">Guides</p>
        <h1 className="mt-2 font-display text-4xl leading-tight md:text-5xl">Financial Planning Guides</h1>
        <p className="mt-3 max-w-3xl text-sm text-muted-foreground md:text-base">
          Start with practical playbooks that pair with Fire-Calc calculators. Each guide focuses on a high-impact
          money decision and links directly to tools you can use to test assumptions, compare scenarios, and create
          an action plan.
        </p>
      </section>

      <section className="grid gap-4 md:grid-cols-3">
        {guideRoutes.map((guideRoute) => (
          <Card key={guideRoute.path} className="h-full">
            <CardHeader>
              <p className="text-xs font-semibold uppercase tracking-[0.12em] text-muted-foreground">Guide</p>
              <CardTitle>{guideRoute.title}</CardTitle>
              <CardDescription>{guideRoute.description}</CardDescription>
            </CardHeader>
            <CardContent>
              <Button asChild size="sm">
                <Link to={guideRoute.path}>Open Guide</Link>
              </Button>
            </CardContent>
          </Card>
        ))}
      </section>

      <section className="brand-frame rounded-2xl border border-border bg-card/85 p-6">
        <h2 className="font-display text-2xl text-foreground">Supporting Resources</h2>
        <p className="mt-2 text-sm text-muted-foreground">
          Use these references to validate assumptions and keep your planning inputs consistent across calculators.
        </p>
        <ul className="mt-4 grid gap-2 text-sm md:grid-cols-2">
          {[...staticContentLinks.about, ...staticContentLinks.resources].map((link) => (
            <li key={link.href}>
              <Link to={link.href} className="font-semibold text-primary underline decoration-primary/45 underline-offset-4 hover:text-foreground">
                {link.label}
              </Link>
            </li>
          ))}
        </ul>
      </section>

      <RouteNextSteps canonicalPath={route.canonicalPath} />
    </AppLayout>
  );
}
